import React, { createContext, FC, ReactNode, useCallback, useContext, useState } from 'react';

interface Viewer3DContextValue {
    /** Register the capture function from the 3D viewer */
    registerCapture: (captureFn: () => Promise<string | null>) => void;
    /** Capture a screenshot and return as base64 data URL */
    capture: () => Promise<string | null>;
    /** Whether a capture is in progress */
    isCapturing: boolean;
}

const Viewer3DContext = createContext<Viewer3DContextValue | null>(null);

interface Viewer3DProviderProps {
    children: ReactNode;
}

export const Viewer3DProvider: FC<Viewer3DProviderProps> = ({ children }) => {
    const [captureFn, setCaptureFn] = useState<(() => Promise<string | null>) | null>(null);
    const [isCapturing, setIsCapturing] = useState(false);

    const registerCapture = useCallback((fn: () => Promise<string | null>) => {
        setCaptureFn(() => fn);
    }, []);

    const capture = useCallback(async (): Promise<string | null> => {
        if (!captureFn) return null;

        setIsCapturing(true);
        try {
            // Return base64 data URL directly from the 3D viewer
            const dataUrl = await captureFn();
            return dataUrl;
        } catch (error) {
            console.error('3D capture failed:', error);
            return null;
        } finally {
            setIsCapturing(false);
        }
    }, [captureFn]);

    return <Viewer3DContext.Provider value={{ registerCapture, capture, isCapturing }}>{children}</Viewer3DContext.Provider>;
};

/**
 * Hook to access 3D viewer capture functionality.
 * Returns null if not within a Viewer3DProvider (not in 3D mode).
 */
export const useViewer3DCapture = () => {
    return useContext(Viewer3DContext);
};
